import { z } from 'zod';

const SymbolResult = z.object({
  isin: z.string().nullable(),
  title: z.string().nullable(),
  name: z.string().nullable(),
  isActive: z.boolean(),
  sectorCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  instrumentStateTitle: z.string().nullable(),
  instrumentStateCode: z.string().nullable(),
  instrumentTradingState: z.number(),
  color: z.string().nullable(),
  instrumentColorIndex: z.number().nullable(),
  instrumentTypeCode: z.string().nullable(),
  market: z.string().nullable(),
});

const SearchSymbolsQuery = z.object({
  items: SymbolResult.array(),
});

const GroupedSymbols = z.object({
  share: SymbolResult.array(),
  shareRight: SymbolResult.array(),
  ETF: SymbolResult.array(),
  commodityDepositCertificate: SymbolResult.array(),
  mortgageBond: SymbolResult.array(),
  treasuryBond: SymbolResult.array(),
  debtBond: SymbolResult.array(),
  others: SymbolResult.array(),
});

const BidAsk = z.object({
  id: z.onumber(),
  askQuantity: z.number(),
  askNumber: z.number(),
  askPrice: z.number(),
  bidPrice: z.number(),
  bidNumber: z.number(),
  bidQuantity: z.number(),
  instrumentId: z.number(),
  updateDate: z.string(),
  rowPlace: z.number(),
});

const Instrument = z.object({
  baseQuantity: z.number().nullable(),
  buyFirmCount: z.number().nullable(),
  buyFirmVolume: z.number().nullable(),
  buyFirmVolumePercentage: z.number().nullable(),
  buyGroupCount: z.number().nullable(),
  buyGroupVolume: z.number().nullable(),
  buyGroupVolumePercentage: z.number().nullable(),
  buyIndividualCount: z.number().nullable(),
  buyIndividualVolume: z.number().nullable(),
  buyIndividualVolumePercentage: z.number().nullable(),
  closingPrice: z.number().nullable(),
  closingPriceVariation: z.number().nullable(),
  closingPriceVariationPercent: z.number().nullable(),
  exchangeName: z.string().nullable(),
  exchangeCode: z.string().nullable(),
  firstTradePrice: z.number().nullable(),
  highestTradePrice: z.number().nullable(),
  instrumentId: z.number().nullable(),
  instrumentName: z.string().nullable(),
  instrumentTitle: z.string().nullable(),
  instrumentCode: z.string().nullable(),
  instrumentStateCode: z.string().nullable(),
  instrumentTradingState: z.number(),
  instrumentStateTitle: z.string().nullable(),
  isin: z.string().nullable(),
  lastTradeFormatedDate: z.string().nullable(),
  lastTradeDate: z.string().nullable(),
  lastTradePrice: z.number().nullable(),
  lowerPriceThreshold: z.number().nullable(),
  lowestTradePrice: z.number().nullable(),
  previousDayPrice: z.number().nullable(),
  referencePriceVariation: z.number().nullable(),
  referencePriceVariationPercentage: z.number().nullable(),
  selFirmCount: z.number().nullable(),
  selFirmVolume: z.number().nullable(),
  selFirmVolumePercentage: z.number().nullable(),
  selIndividualCount: z.number().nullable(),
  selIndividualVolume: z.number().nullable(),
  selIndividualVolumePercentage: z.number().nullable(),
  subSectorCode: z.string().nullable(),
  totalNumberOfSharesTraded: z.number().nullable(),
  totalNumberOfTrades: z.number().nullable(),
  totalTradeValue: z.number().nullable(),
  upperPriceThreshold: z.number().nullable(),
  eps: z.number().nullable(),
  pricePerEarningGroup: z.number().nullable(),
  pricePerEarning: z.number().nullable(),
  freeFloatPercent: z.number().nullable(),
  monthAverageVolume: z.number().nullable(),
  instrumentMarketValue: z.number().nullable(),
  numberOfSharesOrBonds: z.number().nullable(),
  minimumOrderQuantity: z.number().nullable(),
  maximumOrderQuantity: z.number().nullable(),
  yearHighestTradePrice: z.number().nullable(),
  yearLowestTradePrice: z.number().nullable(),
  referencePrice: z.number().nullable(),
  sectorCode: z.string().nullable(),
  amountOfParValue: z.number().nullable(),
  instrumentTypeCode: z.string().nullable(),
  instrumentTypeTitle: z.string().nullable(),
  instrumentColorIndex: z.number().nullable(),
  color: z.string().nullable(),
  lastTradeTime: z.string().nullable(),
  tmcId: z.string().nullable(),
  priceOfRedemptionNav: z.number().nullable(),
  navDateOfEvent: z.string().nullable(),
  bidAsk: BidAsk.array(),
  lotSize: z.number(),
  fixedPriceTick: z.number(),
  minBuyOrderValue: z.number(),
  minSellOrderValue: z.number(),
});

const SymbolDeductionsResult = z.object({
  exchangeCode: z.string().nullable(),
  buyMaximumValue: z.number(),
  sellMaximumValue: z.number(),
  buyValue: z.number(),
  sellValue: z.number(),
  instrumentTypeCodesField: z.string().array().nullable(),
  sectorCodesField: z.string().array().nullable(),
  subSectorCodesField: z.string().array().nullable(),
});

const SymbolDeductionsQuery = z.object({
  totalCount: z.number(),
  items: SymbolDeductionsResult.array(),
});

const GetBrokerChangeItem = z.object({
  isin: z.string().nullable(),
  requestDate: z.string(),
  requestPersianDate: z.string().nullable(),
  requestEntryDate: z.string(),
  requestPersianEntryDate: z.string().nullable(),
  requestState: z.string().nullable(),
  requestStateId: z.number(),
  requestDescription: z.string().nullable(),
  requestId: z.number(),
  requestSerialNumber: z.string().nullable(),
  instrumentName: z.string().nullable(),
  attachment: z.number().nullable(),
});

const GetBrokerChangeQuery = z.object({
  totalCount: z.number(),
  items: GetBrokerChangeItem.array().nullable(),
});

const SymbolCandleStickDetail = z.object({
  o: z.number().nullable(),
  h: z.number(),
  l: z.number(),
  c: z.number().nullable(),
  v: z.number().nullable(),
  oTime: z.number(),
});

const SymbolCandleStickResult = z.object({
  candleStickDetails: z.array(SymbolCandleStickDetail).nullable(),
});

const InstrumentSectorDetail = z.object({
  isin: z.string().nullable(),
  name: z.string().nullable(),
  sectorCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  instrumentTypeCode: z.number().nullable(),
  instrumentTitle: z.string().nullable(),
  instrumentGroupId: z.string().nullable(),
  referencePriceVariationPercentage: z.string().nullable(),
  tradePrice: z.number().nullable(),
  lastTradePrice: z.number().nullable(),
  closingPriceVariationPercentage: z.number().nullable(),
  instrumentStateCode: z.string().nullable(),
  instrumentTradingState: z.number(),
  persionStateTitle: z.string().nullable(),
  englishStateTitle: z.string().nullable(),
  individualBuyVolume: z.number(),
  individualSellVolume: z.number(),
  individualBuyCount: z.number(),
  individualSellCount: z.number(),
  firmBuyVolume: z.number(),
  firmSellVolume: z.number(),
  totalNumberOfSharesTraded: z.number(),
  totalTradeValue: z.number(),
  closingPrice: z.number(),
  tradeNumber: z.number(),
  topVariation: z.number().optional(),
  topVariationPercentage: z.number(),
});

const InstrumentSectorResult = z.object({
  result: InstrumentSectorDetail.array().nullable(),
});

const SubSector = z.object({
  code: z.string().nullable(),
  name: z.string().nullable(),
});

const SectorDetail = z.object({
  code: z.string().nullable(),
  name: z.string().nullable(),
  popular: z.boolean().nullable(),
  rank: z.number().nullable(),
  shortName: z.string().nullable(),
  insertDateTime: z.string(),
  subSectors: SubSector.array().nullable(),
});

const SectorResult = z.object({
  totalCount: z.number().nullable(),
  items: SectorDetail.array().nullable(),
});

type SymbolResult = z.infer<typeof SymbolResult>;
type SearchSymbolsQuery = z.infer<typeof SearchSymbolsQuery>;
type GroupedSymbols = z.infer<typeof GroupedSymbols>;
type Instrument = z.infer<typeof Instrument>;
type SymbolDeductionsQuery = z.infer<typeof SymbolDeductionsQuery>;
type BidAsk = z.infer<typeof BidAsk>;
type GetBrokerChangeItem = z.infer<typeof GetBrokerChangeItem>;
type GetBrokerChangeQuery = z.infer<typeof GetBrokerChangeQuery>;
type SymbolCandleStickDetail = z.infer<typeof SymbolCandleStickDetail>;
type SymbolCandleStickResult = z.infer<typeof SymbolCandleStickResult>;
type InstrumentSectorDetail = z.infer<typeof InstrumentSectorDetail>;
type InstrumentSectorResult = z.infer<typeof InstrumentSectorResult>;
type SectorResult = z.infer<typeof SectorResult>;
type SectorDetail = z.infer<typeof SectorDetail>;

export {
  SymbolResult,
  SearchSymbolsQuery,
  GroupedSymbols,
  Instrument,
  SymbolDeductionsQuery,
  BidAsk,
  GetBrokerChangeItem,
  GetBrokerChangeQuery,
  SymbolCandleStickDetail,
  SymbolCandleStickResult,
  InstrumentSectorResult,
  InstrumentSectorDetail,
  SectorResult,
  SectorDetail,
};
